'use client';

import { c as _c } from "react/compiler-runtime";
import { isLiveActivityPath, LIVE_ACTIVITY_PAGE_TABS, LIVE_ACTIVITY_TAB_VALUES, type LiveActivityPageTabs } from '@/app/(authenticated)/(dashboard)/live-activity/constants';
import { DEFAULT_ROI_SETTINGS_TAB } from '@/domains/reports/report-page/roi/roi-settings-tabs';
import { useDefaultSettingsSection } from '@/domains/settings/use-settings-sidebar-items';
import { BasicTooltip } from '@/comps/basic-tooltip';
import { DialogConfirm } from '@/comps/dialog-confirm';
import { ListItem } from '@/comps/list';
import { Popover, PopoverContent, PopoverTrigger } from '@/comps/primitives/popover';
import { PARAMS_KEY_ORGANIZATION_ID, PARAMS_KEY_PROJECT_ID, PARAMS_KEY_REPORT_ID, PARAMS_KEY_REPORT_SIDEBAR_SECTION, PARAMS_KEY_SUBPRODUCT } from '@/constants/route-params-keys';
import { GlobalFilterPicker } from '@/domains/dashboard/components/GlobalfiltersRow';
import { getHueCircleBackground, getHueCircleOutlineColor, type HueCircleStyle } from '@/domains/dashboard/components/RandomHueCircle';
import { getProjectProductConfig } from '@/domains/dashboard/components/project-product.config';
import { ProjectSelect } from '@/domains/dashboard/components/ProjectSelect';
import { useRailActiveStyles, RAIL_BOTTOM_ICON_BUTTON_ACTIVE_CLASS, RAIL_BOTTOM_ICON_BUTTON_BASE_CLASS, RAIL_BOTTOM_ICON_BUTTON_CLASS, RAIL_CENTER_ICON_BUTTON_ACTIVE_CLASS, RAIL_CENTER_ICON_BUTTON_CLASS, RAIL_CENTER_ICON_CLASS, RAIL_CENTER_LABEL_ACTIVE_CLASS, RAIL_CENTER_LABEL_CLASS, RAIL_CENTER_LABELED_BUTTON_CLASS, RAIL_CENTER_LABELED_BUTTON_DISABLED_CLASS, RAIL_ICON_POPUP_SIDE_OFFSET, RAIL_TOOLTIP_SURFACE_CLASS } from '@/components/layout/dashboard/app-shell-rail-product-styles';
import { AppShellRailItemTooltip, AppShellRailTooltipContent } from '@/components/layout/dashboard/app-shell-rail-tooltip-content';
import { jetbrainsMono } from '@/theme/utils/font.utils';
import { useAppNavigation } from '@/domains/navigation/use-app-navigation';
import { formatReportEditorName, getDisplayNameInitials, getReportEditorInitials } from '@/domains/reports/utils/report-header.utils';
import { useAuthContext } from '@/hooks/use-auth';
import { useConfig } from '@/hooks/use-config';
import { useLogout } from '@/hooks/use-logout';
import { useLocalStorageState } from '@/hooks/useSessionState';
import type { ProjectProduct } from '@/models/organizations/internal-models';
import { useProjectsContext } from '@/providers/projects-provider';
import { cn } from '@/utils/shad';
import { Calculator, CircleUser, Database, Filter, HelpCircle, ListTree, LogOut, Settings } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { forwardRef, useState, type ComponentPropsWithoutRef, type CSSProperties, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
const LAST_LIVE_ACTIVITY_TAB_STORAGE_KEY = 'nebuly_last_live_activity_tab';
const SIDEBAR_URL_KEEP = [PARAMS_KEY_REPORT_ID, PARAMS_KEY_REPORT_SIDEBAR_SECTION];
type RailBottomIconButtonProps = ComponentPropsWithoutRef<'button'> & {
  isActive?: boolean;
  isInteractive?: boolean;
  disableHoverStyles?: boolean;
};
function RailBottomIconButton(t0) {
  const $ = _c(15);
  let children;
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      isActive: t1,
      isInteractive: t2,
      disableHoverStyles: t3,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
  }
  const isActive = t1 === undefined ? false : t1;
  const isInteractive = t2 === undefined ? false : t2;
  const disableHoverStyles = t3 === undefined ? false : t3;
  const {
    isActive: isActiveStyled
  } = useRailActiveStyles(isActive);
  const t4 = disableHoverStyles ? RAIL_BOTTOM_ICON_BUTTON_BASE_CLASS : RAIL_BOTTOM_ICON_BUTTON_CLASS;
  const t5 = (isActiveStyled || isInteractive) && RAIL_BOTTOM_ICON_BUTTON_ACTIVE_CLASS;
  let t6;
  if ($[7] !== className || $[8] !== t4 || $[9] !== t5) {
    t6 = cn(t4, t5, className);
    $[7] = className;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== children || $[12] !== props || $[13] !== t6) {
    t7 = <button type="button" className={t6} {...props}>{children}</button>;
    $[11] = children;
    $[12] = props;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}
type RailCenterLabeledButtonProps = ComponentPropsWithoutRef<'button'> & {
  icon: LucideIcon;
  label: string;
  isActive?: boolean;
  isDisabled?: boolean;
  allowActiveWhenDisabled?: boolean;
  iconBadge?: ReactNode;
};
export function RailCenterLabeledButton(t0) {
  const $ = _c(33);
  let Icon;
  let className;
  let iconBadge;
  let label;
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      icon: Icon,
      label,
      isActive: t1,
      isDisabled: t2,
      allowActiveWhenDisabled: t3,
      iconBadge,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = Icon;
    $[2] = className;
    $[3] = iconBadge;
    $[4] = label;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    Icon = $[1];
    className = $[2];
    iconBadge = $[3];
    label = $[4];
    props = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
  }
  const isActive = t1 === undefined ? false : t1;
  const isDisabled = t2 === undefined ? false : t2;
  const allowActiveWhenDisabled = t3 === undefined ? false : t3;
  const {
    isActive: isActiveStyled
  } = useRailActiveStyles(isActive, isDisabled, allowActiveWhenDisabled);
  const t4 = isDisabled && !isActiveStyled && RAIL_CENTER_LABELED_BUTTON_DISABLED_CLASS;
  let t5;
  if ($[9] !== className || $[10] !== t4) {
    t5 = cn(RAIL_CENTER_LABELED_BUTTON_CLASS, t4, className);
    $[9] = className;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = isActive && (!isDisabled || allowActiveWhenDisabled) ? "page" : undefined;
  const t7 = isDisabled || undefined;
  const t8 = isActiveStyled && RAIL_CENTER_ICON_BUTTON_ACTIVE_CLASS;
  let t9;
  if ($[12] !== t8) {
    t9 = cn(RAIL_CENTER_ICON_BUTTON_CLASS, t8);
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== Icon) {
    t10 = <Icon className={RAIL_CENTER_ICON_CLASS} strokeWidth={1.8} aria-hidden={true} />;
    $[14] = Icon;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== iconBadge || $[17] !== t10 || $[18] !== t9) {
    t11 = <span className={t9}>{t10}{iconBadge}</span>;
    $[16] = iconBadge;
    $[17] = t10;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const t12 = isActiveStyled && RAIL_CENTER_LABEL_ACTIVE_CLASS;
  let t13;
  if ($[20] !== t12) {
    t13 = cn(RAIL_CENTER_LABEL_CLASS, t12);
    $[20] = t12;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== label || $[23] !== t13) {
    t14 = <span className={t13}>{label}</span>;
    $[22] = label;
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== label || $[26] !== props || $[27] !== t11 || $[28] !== t14 || $[29] !== t5 || $[30] !== t6 || $[31] !== t7) {
    t15 = <button type="button" className={t5} aria-label={label} aria-current={t6} aria-disabled={t7} {...props}>{t11}{t14}</button>;
    $[25] = label;
    $[26] = props;
    $[27] = t11;
    $[28] = t14;
    $[29] = t5;
    $[30] = t6;
    $[31] = t7;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  return t15;
}
function getUserDisplayName(email: string | null | undefined): string {
  const localPart = formatReportEditorName(email);
  if (localPart === 'Unknown') return localPart;
  return localPart.replace(/[._-]+/g, ' ').split(/\s+/).filter(Boolean).map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()).join(' ');
}
const NEBULY_DOCS_URL = 'https://docs.nebuly.com/guides/what-is-nebuly';
type ProjectPickerRailTriggerProps = ComponentPropsWithoutRef<'button'> & {
  projectName: string;
  projectProduct: ProjectProduct;
  organizationHueStyle?: HueCircleStyle;
};
const ProjectPickerRailTrigger = forwardRef<HTMLButtonElement, ProjectPickerRailTriggerProps>(function ProjectPickerRailTrigger(t0, ref) {
  const $ = _c(39);
  let className;
  let disabled;
  let organizationHueStyle;
  let projectName;
  let projectProduct;
  let props;
  if ($[0] !== t0) {
    ({
      disabled,
      projectName,
      projectProduct,
      organizationHueStyle,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = disabled;
    $[3] = organizationHueStyle;
    $[4] = projectName;
    $[5] = projectProduct;
    $[6] = props;
  } else {
    className = $[1];
    disabled = $[2];
    organizationHueStyle = $[3];
    projectName = $[4];
    projectProduct = $[5];
    props = $[6];
  }
  let t1;
  if ($[7] !== projectProduct) {
    t1 = getProjectProductConfig(projectProduct);
    $[7] = projectProduct;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const {
    icon: Icon,
    title: productTitle
  } = t1;
  let t2;
  if ($[9] !== organizationHueStyle) {
    t2 = organizationHueStyle ? getHueCircleOutlineColor(organizationHueStyle) : "var(--color-border)";
    $[9] = organizationHueStyle;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const outlineColor = t2;
  let t3;
  if ($[11] !== className) {
    t3 = cn("group flex w-full cursor-pointer flex-col items-center gap-[3px] rounded-md pt-2 disabled:pointer-events-none disabled:opacity-50", className);
    $[11] = className;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const t4 = !organizationHueStyle && "border border-border bg-muted text-muted-foreground";
  let t5;
  if ($[13] !== t4) {
    t5 = cn("inline-flex size-[34px] items-center justify-center rounded-[10px] outline-1 outline-offset-2 outline-transparent transition-[outline-color] group-hover:outline-[color:var(--rail-tile-outline)] group-data-[state=open]:outline-[color:var(--rail-tile-outline)]", t4);
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== organizationHueStyle) {
    t6 = organizationHueStyle ? {
      background: getHueCircleBackground(organizationHueStyle)
    } : {};
    $[15] = organizationHueStyle;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== outlineColor || $[18] !== t6) {
    t7 = {
      "--rail-tile-outline": outlineColor,
      ...t6
    };
    $[17] = outlineColor;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const t8 = t7 as CSSProperties;
  const t9 = organizationHueStyle && "text-zinc-950";
  let t10;
  if ($[20] !== t9) {
    t10 = cn("size-[17px]", t9);
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== Icon || $[23] !== t10) {
    t11 = <Icon className={t10} strokeWidth={2.1} aria-hidden={true} />;
    $[22] = Icon;
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== productTitle || $[26] !== t11 || $[27] !== t5 || $[28] !== t8) {
    t12 = <span className="relative inline-flex shrink-0"><span className={t5} style={t8} title={productTitle}>{t11}</span></span>;
    $[25] = productTitle;
    $[26] = t11;
    $[27] = t5;
    $[28] = t8;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const t13 = projectName || "(no project)";
  let t14;
  if ($[30] !== t13) {
    t14 = <span className="w-full truncate px-1.5 text-center text-[9px] tracking-[-0.1px] text-zinc-400">{t13}</span>;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== disabled || $[33] !== props || $[34] !== ref || $[35] !== t12 || $[36] !== t14 || $[37] !== t3) {
    t15 = <button ref={ref} type="button" className={t3} disabled={disabled} {...props}>{t12}{t14}</button>;
    $[32] = disabled;
    $[33] = props;
    $[34] = ref;
    $[35] = t12;
    $[36] = t14;
    $[37] = t3;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  return t15;
});
export function AppShellRailProjectPicker() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <ProjectSelect className="w-full" popoverSide="right" popoverAlign="start" renderTrigger={_temp} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp(t0) {
  const {
    disabled,
    projectName,
    projectProduct,
    organizationHueStyle
  } = t0;
  return <ProjectPickerRailTrigger disabled={disabled} projectName={projectName} projectProduct={projectProduct} organizationHueStyle={organizationHueStyle} data-tour="sidebar-project-picker" data-testid="rail-project-select-trigger" />;
}
export function AppShellRailDocsHelpButton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <BasicTooltip text="Documentation" side="right" sideOffset={RAIL_ICON_POPUP_SIDE_OFFSET} classNameContent={RAIL_TOOLTIP_SURFACE_CLASS}><a href={NEBULY_DOCS_URL} target="_blank" rel="noopener noreferrer" className={RAIL_BOTTOM_ICON_BUTTON_CLASS} aria-label="Open Nebuly documentation"><HelpCircle className={RAIL_CENTER_ICON_CLASS} aria-hidden={true} /></a></BasicTooltip>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function AppShellRailSettingsButton() {
  const $ = _c(10);
  const pathname = usePathname();
  const {
    navigateApp
  } = useAppNavigation();
  const defaultSection = useDefaultSettingsSection();
  let t0;
  if ($[0] !== pathname) {
    t0 = pathname.startsWith("/settings");
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isActive = t0;
  const t1 = isActive ? "page" : undefined;
  let t2;
  if ($[2] !== defaultSection || $[3] !== navigateApp) {
    t2 = () => {
      navigateApp({
        route: "settings-section",
        section: defaultSection,
        keepList: [PARAMS_KEY_ORGANIZATION_ID, PARAMS_KEY_PROJECT_ID]
      });
    };
    $[2] = defaultSection;
    $[3] = navigateApp;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Settings className={RAIL_CENTER_ICON_CLASS} aria-hidden={true} />;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== isActive || $[7] !== t1 || $[8] !== t2) {
    t4 = <BasicTooltip text="Settings" side="right" sideOffset={RAIL_ICON_POPUP_SIDE_OFFSET} classNameContent={RAIL_TOOLTIP_SURFACE_CLASS}><RailBottomIconButton isActive={isActive} aria-label="Settings" aria-current={t1} onClick={t2}>{t3}</RailBottomIconButton></BasicTooltip>;
    $[6] = isActive;
    $[7] = t1;
    $[8] = t2;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
export function AppShellRailUserAvatar() {
  const $ = _c(37);
  const {
    email,
    fullName
  } = useAuthContext();
  const {
    logout
  } = useLogout();
  const {
    navigateApp
  } = useAppNavigation();
  const [open, setOpen] = useState(false);
  let t0;
  if ($[0] !== email || $[1] !== fullName) {
    t0 = fullName ?? getUserDisplayName(email);
    $[0] = email;
    $[1] = fullName;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const displayName = t0;
  let t1;
  if ($[3] !== email || $[4] !== fullName) {
    t1 = fullName ? getDisplayNameInitials(fullName) : getReportEditorInitials(email);
    $[3] = email;
    $[4] = fullName;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const initials = t1;
  const t2 = open ? "border-[rgba(140,147,217,0.52)]" : "border-[#3f3f46]";
  let t3;
  if ($[6] !== t2) {
    t3 = cn("flex size-6 items-center justify-center rounded-full border bg-[#1c1c22] text-[9px] leading-none text-[#a1a1aa] transition-colors", t2, jetbrainsMono.className);
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== initials || $[9] !== t3) {
    t4 = <span className={t3} aria-hidden={true}>{initials}</span>;
    $[8] = initials;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== open || $[12] !== t4) {
    t5 = <PopoverTrigger asChild={true}><RailBottomIconButton aria-label="Account menu" isInteractive={open} data-testid="account-menu-trigger">{t4}</RailBottomIconButton></PopoverTrigger>;
    $[11] = open;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== displayName) {
    t6 = <div className="text-sm font-semibold text-foreground">{displayName}</div>;
    $[14] = displayName;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== email) {
    t7 = email ? <div className="mt-[3px] text-xs text-muted-foreground">{email}</div> : null;
    $[16] = email;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t6 || $[19] !== t7) {
    t8 = <div className="-mx-[5px] mb-[5px] border-b border-border px-[15px] pt-2.5 pb-2.5 whitespace-nowrap">{t6}{t7}</div>;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== navigateApp) {
    t9 = () => {
      navigateApp({
        route: "settings-section",
        section: "account",
        keepList: [PARAMS_KEY_ORGANIZATION_ID, PARAMS_KEY_PROJECT_ID]
      });
    };
    $[21] = navigateApp;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span className="flex items-center gap-2 whitespace-nowrap"><CircleUser className="size-4" aria-hidden={true} />Account Settings</span>;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t9) {
    t11 = <ListItem className="rounded-md px-4 hover:brightness-90" onClick={t9}>{t10}</ListItem>;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ListItem className="rounded-md px-4 hover:brightness-90" data-testid="account-menu-logout-button"><span className="flex items-center gap-2 whitespace-nowrap"><LogOut className="size-4" aria-hidden={true} />Logout</span></ListItem>;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== logout) {
    t13 = <DialogConfirm description="Are you sure you want to exit from the platform?" title="Logout" onConfirm={logout} confirmTestId="account-menu-logout-confirm">{t12}</DialogConfirm>;
    $[27] = logout;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t11 || $[30] !== t13 || $[31] !== t8) {
    t14 = <PopoverContent className="w-auto bg-background p-[5px] shadow-neutral-900" side="right" align="end" sideOffset={RAIL_ICON_POPUP_SIDE_OFFSET} onOpenAutoFocus={_temp2} onCloseAutoFocus={_temp3}>{t8}{t11}{t13}</PopoverContent>;
    $[29] = t11;
    $[30] = t13;
    $[31] = t8;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== open || $[34] !== t14 || $[35] !== t5) {
    t15 = <Popover open={open} onOpenChange={setOpen} modal={true}>{t5}{t14}</Popover>;
    $[33] = open;
    $[34] = t14;
    $[35] = t5;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  return t15;
}
function _temp3(event_0) {
  return event_0.preventDefault();
}
function _temp2(event) {
  return event.preventDefault();
}
export function AppShellRailLiveDataButton() {
  const $ = _c(11);
  const pathname = usePathname();
  const {
    navigateApp
  } = useAppNavigation();
  const [lastLiveActivityTab, setLastLiveActivityTab] = useLocalStorageState(LAST_LIVE_ACTIVITY_TAB_STORAGE_KEY, LIVE_ACTIVITY_PAGE_TABS.interactions);
  let t0;
  if ($[0] !== lastLiveActivityTab) {
    t0 = LIVE_ACTIVITY_TAB_VALUES.includes(lastLiveActivityTab) ? lastLiveActivityTab : LIVE_ACTIVITY_PAGE_TABS.interactions;
    $[0] = lastLiveActivityTab;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const resolvedLastLiveActivityTab = t0;
  let t1;
  if ($[2] !== pathname) {
    t1 = isLiveActivityPath(pathname);
    $[2] = pathname;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const isActive = t1;
  let t2;
  if ($[4] !== navigateApp || $[5] !== resolvedLastLiveActivityTab || $[6] !== setLastLiveActivityTab) {
    t2 = () => {
      setLastLiveActivityTab(resolvedLastLiveActivityTab);
      navigateApp({
        route: "live-activity-section",
        section: resolvedLastLiveActivityTab,
        keepList: SIDEBAR_URL_KEEP
      });
    };
    $[4] = navigateApp;
    $[5] = resolvedLastLiveActivityTab;
    $[6] = setLastLiveActivityTab;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== isActive || $[9] !== t2) {
    t3 = <RailCenterLabeledButton icon={Database} label="Live data" isActive={isActive} data-tour="sidebar-live-data-nav" onClick={t2} />;
    $[8] = isActive;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}
export function AppShellRailTaxonomyButton() {
  const $ = _c(5);
  const pathname = usePathname();
  const {
    navigateApp
  } = useAppNavigation();
  const isActive = pathname === "/taxonomy";
  let t0;
  if ($[0] !== navigateApp) {
    t0 = () => {
      navigateApp({
        route: "/taxonomy",
        keepList: [PARAMS_KEY_ORGANIZATION_ID, PARAMS_KEY_PROJECT_ID, PARAMS_KEY_SUBPRODUCT, ...SIDEBAR_URL_KEEP]
      });
    };
    $[0] = navigateApp;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== isActive || $[3] !== t0) {
    t1 = <RailCenterLabeledButton icon={ListTree} label="Taxonomy" isActive={isActive} onClick={t0} />;
    $[2] = isActive;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
export function AppShellRailRoiSetupButton() {
  const $ = _c(7);
  const pathname = usePathname();
  const {
    navigateApp
  } = useAppNavigation();
  const {
    selectedProject,
    projectId
  } = useProjectsContext();
  const originalProjectId = selectedProject?.mappedProjectId ?? projectId;
  const {
    data: projectConfig
  } = useConfig(originalProjectId);
  const isRoiEnabled = projectConfig?.roi_settings?.enabled ?? false;
  let t0;
  if ($[0] !== pathname) {
    t0 = pathname.startsWith("/roi-setup");
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isActive = t0;
  if (!isRoiEnabled || !originalProjectId || selectedProject?.product !== "internal") {
    return null;
  }
  let t1;
  if ($[2] !== navigateApp) {
    t1 = () => {
      navigateApp({
        route: "roi-setup-section",
        section: DEFAULT_ROI_SETTINGS_TAB,
        keepList: [PARAMS_KEY_ORGANIZATION_ID, PARAMS_KEY_PROJECT_ID]
      });
    };
    $[2] = navigateApp;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== isActive || $[5] !== t1) {
    t2 = <RailCenterLabeledButton icon={Calculator} label="ROI Setup" isActive={isActive} onClick={t1} />;
    $[4] = isActive;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
export function AppShellRailGlobalFiltersItem() {
  const $ = _c(2);
  const {
    selectedProject
  } = useProjectsContext();
  if (!selectedProject?.id) {
    return null;
  }
  let t0;
  if ($[0] !== selectedProject.id) {
    t0 = <div className="w-full" data-tour="sidebar-filters-nav"><GlobalFilterPicker projectId={selectedProject.id} popoverSide="right" renderTrigger={_temp5} /></div>;
    $[0] = selectedProject.id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function _temp5(t0) {
  const {
    disabled,
    appliedCount
  } = t0;
  return <AppShellRailItemTooltip renderContent={_temp4}><RailCenterLabeledButton icon={Filter} label="Filters" disabled={disabled} iconBadge={appliedCount > 0 ? <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-n-violet-platform ring-1 ring-background" aria-hidden={true} /> : undefined} /></AppShellRailItemTooltip>;
}
function _temp4() {
  return <AppShellRailTooltipContent title="Filters" icon={Filter} description="Define global filters that apply across all reports." />;
}